import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { FileText, Download, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Support = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('tr') ? 'tr' : 'en';
  const [pageContent, setPageContent] = useState(null);
  const [productsByCategory, setProductsByCategory] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contentRes, productsRes] = await Promise.all([
          fetch(`${API_URL}/api/public/content/support`),
          fetch(`${API_URL}/api/public/products`)
        ]);
        const content = await contentRes.json();
        const products = await productsRes.json();

        setPageContent(content);

        const grouped = {};
        products.forEach(p => {
          const cat = p.category || 'Other';
          if (!grouped[cat]) grouped[cat] = [];
          grouped[cat].push(p);
        });
        setProductsByCategory(grouped);
      } catch (error) {
        console.error('Failed to load support data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getText = (field) => {
    if (typeof field === 'object' && field !== null) {
      return field[lang] || field.en || '';
    }
    return field || '';
  };

  const getFileIcon = (contentType) => {
    if (contentType?.includes('pdf')) return 'PDF';
    if (contentType?.includes('word') || contentType?.includes('document')) return 'DOC';
    if (contentType?.includes('excel') || contentType?.includes('sheet')) return 'XLS';
    return 'FILE';
  };

  useEffect(() => { document.title = 'VIYERO | Destek'; return () => { document.title = 'VIYERO | Ergonomic Solutions'; }; }, []);

  const ProductCard = ({ product }) => {
    const docs = product.documents || [];
    const hasDocuments = docs.length > 0;

    return (
      <Card className="overflow-hidden bg-white border-gray-200 shadow-md hover:shadow-lg transition-shadow" data-testid={`support-product-${product.id}`}>
        <div className="relative overflow-hidden aspect-[16/10] bg-gray-200">
          {product.supportCardImage || product.cardImage || product.images?.[0] ? (
            <>
              <img
                src={product.supportCardImage || product.cardImage || product.images[0]}
                alt={getText(product.displayName) || product.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
              <FileText className="h-8 w-8" />
            </div>
          )}
        </div>
        <CardContent className="p-4 bg-[#cdcbbf]">
          <div className="mb-1">
            <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">
              {product.model}
            </span>
          </div>
          <h3 className="text-sm font-bold text-gray-900 mb-3 whitespace-pre-line">
            {getText(product.displayName) || product.name}
          </h3>
          <div className="space-y-2">
            {hasDocuments ? (
              docs.map(doc => (
                <a
                  key={doc.fileId}
                  href={`${API_URL}/api/files/${doc.fileId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-2 bg-gray-100 rounded-lg hover:bg-blue-50 transition-colors group"
                  data-testid={`download-${doc.fileId}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="bg-blue-600 text-white px-1.5 py-0.5 rounded text-[9px] font-bold">{getFileIcon(doc.contentType)}</span>
                    <span className="text-xs text-gray-700 group-hover:text-blue-700 truncate max-w-[120px]">
                      {doc.label || t('support.userManual')}
                    </span>
                  </div>
                  <span className="text-blue-600 group-hover:text-blue-700 flex items-center gap-1">
                    <Download className="h-3 w-3" />
                    <span className="text-[10px]">{t('support.downloadPdf')}</span>
                  </span>
                </a>
              ))
            ) : (
              <div className="flex items-center justify-between p-2 bg-gray-100 rounded-lg">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-blue-600" />
                  <span className="text-xs text-gray-700">{t('support.userManual')}</span>
                </div>
                <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-6 px-2" disabled>
                  <Download className="h-3 w-3 mr-1" />
                  <span className="text-[10px]">{t('support.comingSoon')}</span>
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
        <Footer />
      </div>
    );
  }

  const heroTitle = getText(pageContent?.hero_title) || t('support.heroTitle');
  const heroDesc = getText(pageContent?.hero_description) || t('support.heroDesc');
  const categories = pageContent?.categories || [];

  return (
    <div className="min-h-screen bg-white" data-testid="support-page">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-24 md:pt-32 pb-4 md:pb-6 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-4">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-testid="support-hero-title">
              {heroTitle}
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto" data-testid="support-hero-desc">
              {heroDesc}
            </p>
          </div>
        </div>
      </section>

      {/* Dynamic Category Sections */}
      {categories.map((cat, idx) => {
        const catProducts = (productsByCategory[cat.key] || []).filter(p => !p.supportHidden);
        if (catProducts.length === 0) return null;

        return (
          <section key={cat.key || idx} className="py-6 md:py-8 bg-white border-t border-gray-100" data-testid={`support-section-${idx}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-8">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                  {getText(cat.label)}
                </h2>
                <p className="text-gray-600 text-sm">
                  {getText(cat.desc)}
                </p>
              </div>

              <div className="flex flex-wrap justify-center gap-4 md:gap-6">
                {catProducts.map((product) => (
                  <div key={product.id} className="w-full sm:w-[calc(50%-12px)] md:w-[calc(33.333%-16px)] lg:w-[calc(25%-18px)]">
                    <ProductCard product={product} />
                  </div>
                ))}
              </div>
            </div>
          </section>
        );
      })}

      {categories.every(cat => (productsByCategory[cat.key] || []).filter(p => !p.supportHidden).length === 0) && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">{t('support.comingSoon')}</p>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default Support;
